/**
 * Persona Adapter
 * Transforms Persona data between API and application layer
 */

import { BaseAdapter } from './base.adapter';
import type {
	Persona,
	PersonaChanges,
	PersonaChangesResponseDto,
	PersonaGeneration,
	CreatePersonaDto,
	PersonaRefineDto,
	PersonaValidateExamplesDto,
	PersonaUpdateGuideDto,
	PersonaDto,
	PersonaGenerationDto,
	PersonaResponseDto,
	PersonasListResponseDto,
	PersonaVersionsResponseDto
} from '@/types';

export class PersonaAdapter extends BaseAdapter {
	static fromDto(dto: PersonaDto): Persona {
		return {
			id: dto._id,
			accountId: dto.accountId,
			userId: dto.userId,
			generationStatus: dto.generationStatus,
			validationStatus: dto.validationStatus,
			currentVersion: dto.currentVersion,
			summary: dto.summary,
			examples: (dto.examples ?? []).map((example) => ({
				exampleId: example.exampleId,
				query: example.query,
				reply: example.reply,
				response: example.response ?? null,
				alternativeText: example.alternativeText ?? null
			})),
			lastValidatedAt: dto.lastValidatedAt ?? null,
			hasValidatedVersion: dto.validatedGuide != null,
			createdBy: dto.createdBy,
			createdAt: dto.createdAt,
			updatedAt: dto.updatedAt
		};
	}

	static fromDetailApi(dto: PersonaResponseDto): Persona {
		return PersonaAdapter.fromDto(dto.persona);
	}

	static fromListApi(dto: PersonasListResponseDto): Persona[] {
		return (dto.personas ?? []).map((persona) => PersonaAdapter.fromDto(persona));
	}

	static fromGenerationDto(dto: PersonaGenerationDto): PersonaGeneration {
		return {
			version: dto.version,
			jobId: dto.jobId,
			markdownS3Key: dto.markdownS3Key,
			triggeredBy: dto.triggeredBy,
			feedbackText: dto.feedbackText ?? null,
			createdAt: dto.createdAt,
			publishedToBrahmaAt: dto.publishedToBrahmaAt ?? null,
			isCurrent: dto.isCurrent
		};
	}

	static fromVersionsApi(dto: PersonaVersionsResponseDto): PersonaGeneration[] {
		return (dto ?? []).map((generation) => PersonaAdapter.fromGenerationDto(generation));
	}

	static fromChangesApi(dto: PersonaChangesResponseDto): PersonaChanges | null {
		const { changes } = dto;
		if (!changes) return null;
		return {
			version: changes.version,
			baseVersion: changes.baseVersion,
			feedbackText: changes.feedbackText ?? null,
			rows: changes.rows.map((row) => ({ type: row.type, text: row.text })),
			review: changes.review
				? {
						status: changes.review.status,
						comments: changes.review.comments.map((comment) => ({
							...comment,
							conflictsWith: comment.conflictsWith ?? null
						}))
					}
				: null
		};
	}

	static toCreateRequest(data: { scope: 'brand' | 'agent'; agentUserIds?: number[] }): CreatePersonaDto {
		return data.scope === 'brand' ? { scope: 'brand' } : { scope: 'agent', agentUserIds: data.agentUserIds };
	}

	static toRefineRequest(feedbackText: string): PersonaRefineDto {
		return { feedbackText: feedbackText.trim() };
	}

	static toUpdateGuideRequest(markdown: string, baseVersion: string): PersonaUpdateGuideDto {
		return { markdown, baseVersion };
	}

	static toValidateExamplesRequest(
		examples: Array<{ exampleId: string; response: 'looks_good' | 'respond_differently'; alternativeText?: string }>
	): PersonaValidateExamplesDto {
		return { examples };
	}
}
